import React, { useState, FormEvent } from "react";

export default function Contact() {
  const [formData, setFormData] = useState({
    meno: "",
    email: "",
    telefon: "",
    sprava: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          meno: formData.meno,
          email: formData.email,
          telefon: formData.telefon,
          sprava: formData.sprava,
          source: "kadernickysalonkatka.pages.dev",
        }),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({ meno: "", email: "", telefon: "", sprava: "" });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section id="kontakt" className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Kontaktujte nás</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Máte otázky alebo si chcete dohodnúť termín? Radi vám pomôžeme
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-3xl p-8 mb-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Kontaktné údaje</h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm">
                    <i className="ri-phone-line text-2xl text-amber-700"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Telefón</div>
                    <a
                      href="tel:+421908942533"
                      className="text-gray-600 hover:text-amber-700 transition-colors"
                    >
                      +421 908 942 533
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm">
                    <i className="ri-mail-line text-2xl text-amber-700"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Email</div>
                    <a
                      href="mailto:info@kadernickysalonkatka.sk"
                      className="text-gray-600 hover:text-amber-700 transition-colors"
                    >
                      info@kadernickysalonkatka.sk
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm">
                    <i className="ri-map-pin-line text-2xl text-amber-700"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Adresa</div>
                   <a
                    href="https://www.google.com/maps/search/?api=1&query=Kollárova+55+Martin+Slovakia"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-amber-700 transition-colors"
                  >
                    Kollárova 55<br />
                    036 01 Martin<br />
                    Slovensko
                  </a>

                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm">
                    <i className="ri-time-line text-2xl text-amber-700"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Otváracie hodiny</div>
                    <p className="text-gray-600">Pondelok - Piatok: na objednávku</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/profile.php?id=100078821591978"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center hover:bg-amber-200 transition-colors cursor-pointer"
              >
                <i className="ri-facebook-fill text-xl text-amber-700"></i>
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center hover:bg-amber-200 transition-colors cursor-pointer"
              >
                <i className="ri-instagram-line text-xl text-amber-700"></i>
              </a>
            </div>
          </div>

          <div>
            <form
              onSubmit={handleSubmit}
              className="bg-gradient-to-br from-amber-50/50 to-white rounded-3xl p-8 shadow-lg border border-amber-100/50"
              id="kontaktny-formular"
            >
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Napíšte nám</h3>

              <div className="space-y-5">
                <div>
                  <label htmlFor="meno" className="block text-sm font-medium text-gray-700 mb-2">
                    Meno *
                  </label>
                  <input
                    type="text"
                    id="meno"
                    name="meno"
                    value={formData.meno}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-amber-700 focus:ring-2 focus:ring-amber-700/20 outline-none transition-all text-sm"
                    placeholder="Vaše meno"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-amber-700 focus:ring-2 focus:ring-amber-700/20 outline-none transition-all text-sm"
                    placeholder="vas@email.sk"
                  />
                </div>

                <div>
                  <label htmlFor="telefon" className="block text-sm font-medium text-gray-700 mb-2">
                    Telefón
                  </label>
                  <input
                    type="tel"
                    id="telefon"
                    name="telefon"
                    value={formData.telefon}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-amber-700 focus:ring-2 focus:ring-amber-700/20 outline-none transition-all text-sm"
                    placeholder="+421 123 456 789"
                  />
                </div>

                <div>
                  <label htmlFor="sprava" className="block text-sm font-medium text-gray-700 mb-2">
                    Správa *
                  </label>
                  <textarea
                    id="sprava"
                    name="sprava"
                    value={formData.sprava}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-amber-700 focus:ring-2 focus:ring-amber-700/20 outline-none transition-all resize-none text-sm"
                    placeholder="Vaša správa (max. 500 znakov)"
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {formData.sprava.length}/500
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-amber-700 text-white py-4 rounded-xl font-semibold hover:bg-amber-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? "Odosiela sa..." : "Odoslať správu"}
                </button>

                {submitStatus === "success" && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl text-sm">
                    Ďakujeme! Vaša správa bola úspešne odoslaná.
                  </div>
                )}

                {submitStatus === "error" && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl text-sm">
                    Nastala chyba pri odosielaní. Skúste to prosím znova.
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
